import { useState } from 'react';
import { Badge, Button, Dialog, Drawer, Sortable, Switch, ThreeDotPopover } from '@dreamtree-org/twreact-ui';
import { GripVertical, Lock, Pencil, Plus, Trash2, Check, MoreVertical } from 'lucide-react';
import { usePhone } from '@/lib/useMediaQuery';
import { STORE_PAGES, MAX_BLOCKS, blockDef } from '@dreamtree-org/landing-kit/schema';
import { Fields } from './FieldEditor';
import { OPTION_TILE, OPTION_TILE_BASE } from './optionTile';
import {
  addBlock, addableBlocks, applyTemplate, blockLabel, isRequired, removeBlock, setBlocks, setSettings, updateBlock,
} from '../../lib/appearance';

/**
 * A template page in Storefront Studio (D64): pick a template, set its options,
 * and arrange its blocks. Required blocks (results, gallery, buy box) can move
 * but never hide or go; content sections (FAQ, promo, trust badges…) slot in
 * between. Everything edits the store document through `onChange(doc)`.
 */
export default function TemplatePanel({ pageKey, doc, onChange, errors = {}, editable, draftId }) {
  const def = STORE_PAGES[pageKey];
  const page = doc.pages[pageKey];
  const [editing, setEditing] = useState(null);
  const phone = usePhone();
  const [adding, setAdding] = useState(false);
  const editingBlock = editing && page.blocks.find((b) => b.id === editing);
  const editingDef = editingBlock && blockDef(pageKey, editingBlock.type);
  const addable = addableBlocks(doc, pageKey);

  return (
    <div className="space-y-5">
      <section className="space-y-2" aria-labelledby={`${pageKey}-tpl`}>
        <h3 id={`${pageKey}-tpl`} className="text-sm font-semibold">Template</h3>
        <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
          {Object.entries(def.templates).map(([key, t]) => {
            const on = page.template === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                disabled={!editable}
                onClick={() => onChange(applyTemplate(doc, pageKey, key))}
                className={`${OPTION_TILE_BASE} relative transition-colors ${on ? 'border-primary-600 bg-primary-50' : 'border-[var(--app-border)] hover:border-primary-400'}`}
              >
                {on ? <Check className="absolute right-2 top-2 h-4 w-4 text-primary-700" aria-hidden="true" /> : null}
                <TemplateSketch blocks={t.blocks} />
                <span className="mt-2 block text-sm font-semibold">{t.label}</span>
                <span className="block text-xs opacity-70">{t.description}</span>
              </button>
            );
          })}
        </div>
        <p className="text-xs opacity-60">Switching template keeps the content sections you added and your block options.</p>
      </section>

      <section className="space-y-2" aria-labelledby={`${pageKey}-opts`}>
        <h3 id={`${pageKey}-opts`} className="text-sm font-semibold">Options</h3>
        <fieldset disabled={!editable}>
          <Fields fields={def.settings} value={page.settings} errors={errors._settings || []} onChange={(settings) => onChange(setSettings(doc, pageKey, settings))} />
        </fieldset>
      </section>

      <section className="space-y-2" aria-labelledby={`${pageKey}-blocks`}>
        <div className="flex items-center justify-between">
          <h3 id={`${pageKey}-blocks`} className="text-sm font-semibold">Blocks</h3>
          <span className="text-xs opacity-60">{page.blocks.length} of {MAX_BLOCKS}</span>
        </div>
        {errors._page ? <ul className="list-disc pl-5 text-sm text-error-600">{errors._page.map((e) => <li key={e.path + e.message}>{e.message}</li>)}</ul> : null}
        <Sortable
          items={page.blocks}
          getKey={(b) => b.id}
          getItemLabel={(b) => blockLabel(pageKey, b)}
          handle
          disabled={!editable}
          onReorder={(blocks) => onChange(setBlocks(doc, pageKey, blocks))}
          renderItem={(b, { handleProps }) => {
            const bd = blockDef(pageKey, b.type);
            const required = isRequired(pageKey, b.type);
            const problems = errors[b.id];
            const label = blockLabel(pageKey, b);
            const actions = [
              bd && bd.fields && bd.fields.length ? { key: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" aria-hidden="true" />, onClick: () => setEditing(b.id) } : null,
              required ? null : { key: 'del', label: 'Delete', tone: 'destructive', icon: <Trash2 className="h-4 w-4" aria-hidden="true" />, disabled: !editable, onClick: () => onChange(removeBlock(doc, pageKey, b.id)) },
            ].filter(Boolean);
            return (
              <div className={`flex items-center gap-2 rounded-lg border p-2 ${problems ? 'border-error-400' : 'border-[var(--app-border)]'} ${b.enabled === false ? 'opacity-60' : ''}`}>
                <span {...handleProps} className={`${handleProps.className || ''} cursor-grab rounded p-1 opacity-60 hover:opacity-100`} role="button" tabIndex={0} aria-label={`Move ${label}`}>
                  <GripVertical className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 truncate text-sm font-medium">
                    {label}
                    {required ? <Lock className="h-3.5 w-3.5 shrink-0 opacity-60" aria-label="Required" /> : null}
                  </span>
                  <span className="block text-xs opacity-60">
                    {bd ? (bd.slot ? 'Store block' : `Section · ${bd.label}`) : b.type}
                    {problems ? ` · ${problems.length} to fix` : ''}
                  </span>
                </span>
                {required ? (
                  <Badge size="xs" variant="outline">Required</Badge>
                ) : (
                  <Switch checked={b.enabled !== false} onChange={(on) => onChange(updateBlock(doc, pageKey, b.id, { enabled: on }))} ariaLabel={`Show ${label}`} disabled={!editable} size="sm" />
                )}
                {actions.length ? (
                  <ThreeDotPopover
                    ariaLabel={`Actions for ${label}`}
                    trigger={<Button size="icon-sm" variant="ghost" aria-label={`Actions for ${label}`}><MoreVertical className="h-4 w-4" aria-hidden="true" /></Button>}
                    items={actions}
                  />
                ) : null}
              </div>
            );
          }}
        />
        <Button variant="outline" fullWidth leftIcon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)} disabled={!editable || !addable.length}>Add block</Button>
      </section>

      <Drawer
        isOpen={!!editingBlock}
        onClose={() => setEditing(null)}
        title={editingDef ? editingDef.label : ''}
        description={editingDef ? editingDef.description : ''}
        placement={phone ? 'bottom' : 'right'}
        size={phone ? 'full' : 'lg'}
        swipeToClose
        footer={<div className="flex justify-end"><Button onClick={() => setEditing(null)}>Done</Button></div>}
      >
        {editingBlock && editingDef ? (
          <fieldset disabled={!editable} className="space-y-4">
            <Fields
              fields={editingDef.fields}
              value={editingBlock.props}
              errors={errors[editingBlock.id] || []}
              draftId={draftId}
              onChange={(props) => onChange(updateBlock(doc, pageKey, editingBlock.id, { props }))}
            />
          </fieldset>
        ) : null}
      </Drawer>

      <Dialog isOpen={adding} onClose={() => setAdding(false)} title={`Add a block to the ${def.label.toLowerCase()}`} size="lg">
        <div className="grid gap-2 sm:grid-cols-2">
          {addable.map((a) => (
            <button key={a.type} type="button" className={OPTION_TILE}
              onClick={() => {
                const r = addBlock(doc, pageKey, a.type);
                onChange(r.doc);
                setAdding(false);
                if (r.block && blockDef(pageKey, a.type).fields.length) setEditing(r.block.id);
              }}>
              <span className="flex items-center gap-2 text-sm font-semibold">{a.label}{a.slot ? <Badge size="xs" variant="primary">Store block</Badge> : null}</span>
              <span className="block text-xs opacity-70">{a.description}</span>
            </button>
          ))}
        </div>
      </Dialog>
    </div>
  );
}

// Block shapes for the template thumbnails: commerce blocks get a recognisable silhouette.
const SKETCH = {
  collection_header: 'h-2 w-2/3',
  toolbar: 'h-1.5 w-full opacity-60',
  filters: 'h-1.5 w-1/2 opacity-60',
  results: 'h-6 w-full',
  gallery: 'h-6 w-full',
  buy_box: 'h-3 w-3/4',
};

/** A tiny wireframe of a template's block order. */
function TemplateSketch({ blocks }) {
  return (
    <span className="flex h-20 flex-col gap-1 overflow-hidden rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2" aria-hidden="true">
      {blocks.slice(0, 7).map((type, i) => (
        <span key={`${type}-${i}`} className={`block shrink-0 rounded-sm bg-primary-200 ${SKETCH[type] || 'h-1.5 w-5/6 opacity-40'}`} />
      ))}
    </span>
  );
}
